import { BaseViewStore } from 'core/stores';
import { CarrierEditForm } from 'modules/carrier/forms';
import { CarrierStore } from 'modules/carrier/stores';
import { LookupStore } from 'modules/lookup/stores';
import { action, observable, makeObservable, computed } from 'mobx';
import { ApplicationStore } from 'platform/application/stores';
import { lookupRouteName, carrierTypes, sourceOfChange } from 'common/constants';
import { formInvalidate, translate, isSuperAdminOrganization, notificationError, isSome } from 'core/utils';
import { BaasicDropdownViewStore } from 'common/stores';
import { CredentialsValidationStore } from 'modules/credentials-validation/stores';
import { removeNullOrEmptyStringProperties } from 'core/utils/removeNullOrEmptyStringProperties';
import _ from 'lodash';
import { localStorageHandler } from 'core/services';

class CarrierEditViewStore extends BaseViewStore {
	@observable isThirdParty = false;
	@observable isPartialThirdParty = false;
	@observable isAuthorized = false;
	@observable isExternalAccount = false;
	carrierTypeDisabled = false;
	carrier = null;
	isEditMode = false;

	form = new CarrierEditForm({
		onSuccess: async () => {
			await this.createOrUpdateCarrier();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.carrierStore = new CarrierStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.credentialsValidationStore = new CredentialsValidationStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.modalTitle =
			this.rootStore.routerStore.routerState.queryParams['create'] !== undefined
				? 'CARRIERVIEW.CREATECARRIER'
				: 'CARRIERVIEW.EDITCARRIER';
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (this.getEditMode() === 'edit') {
			await this.getCarrier(this.rootStore.routerStore.routerState.queryParams.editId, {
				embed: [
					'carrierType',
					'shippingAccount',
					'shippingAccount.carrierType',
					'carrierWarehouses',
					'carrierWarehouses.warehouse',
				],
			});
		} else {
			this.form.clear();
			if (this.isSuperAdminOrganization) {
				this.form.$('sellerId').set('rules', 'required');
			}
		}
		await this.initializeSellerDropdown();
		await Promise.all([
			this.initializeWarehouseDropdown(),
			this.initializeShippingAccountDropdown(),
			this.initializeCarrierTypeDropdown(),
		]);
		this.setBillToFieldsRequired(this.form.$('isPartialThirdParty').value);
		this.resume();
	};

	@action.bound
	initializeWarehouseDropdown = async () => {
		this.warehouseDropdownStore = new BaasicDropdownViewStore();
		await this.warehouseDropdownStore.initialize({
			field: this.form.$('carrierWarehouseIds'),
			value:
				_.get(this.carrier, 'carrierWarehouses.length') > 0
					? this.carrier.carrierWarehouses.map(cw => cw.warehouse)
					: null,
			store: new ApplicationStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
			filterProperties: {
				warehouseOnly: true,
				isOrganizationActive: true,
				includeOnlySellerWarehouses: true,
				embed: ['organizationType'],
				sellerWarehousesOnlyOrganizationId:
					this.getEditMode() === 'edit'
						? _.get(this.carrier, 'sellerId')
						: _.get(this.sellerDropdownStore, 'value.value'),
			},
			isMulti: true,
		});
	};

	@action.bound
	initializeSellerDropdown = async () => {
		if (this.isSuperAdminOrganization && !this.isEditMode) {
			this.sellerDropdownStore = new BaasicDropdownViewStore();
			await this.sellerDropdownStore.initialize({
				field: this.form.$('sellerId'),
				value: localStorageHandler.get('selectedSeller'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				onChangeCallback: value => this.onSelectSeller(value),
			});
		}
	};

	@action.bound
	initializeCarrierTypeDropdown = async () => {
		this.carrierTypeDropdownStore = new BaasicDropdownViewStore();
		await this.carrierTypeDropdownStore.initialize({
			field: this.form.$('carrierTypeId'),
			value: _.get(this.carrier, 'carrierType'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.carrierType,
			onChangeCallback: value => this.onCarrierTypeChange(value, true),
		});
	};

	@action.bound
	initializeShippingAccountDropdown = async () => {
		this.shippingAccountDropdownStore = new BaasicDropdownViewStore();
		await this.shippingAccountDropdownStore.initialize({
			field: this.form.$('shippingAccountId'),
			value:
				this.isEditMode && this.isThirdParty && this.isPartialThirdParty && this.carrier.shippingAccount != null
					? this.carrier.shippingAccount
					: null,
			store: new CarrierStore(this.rootStore),
			filterProperties: {
				page: 1,
				rpp: 200,
				orderDirection: 'asc',
				IsThirdParty: false,
				embed: ['carrierType'],
				...(this.getEditMode() === 'edit' && {
					ExcludedIds: [this.rootStore.routerStore.routerState.queryParams.editId],
					CarrierTypeIds: this.carrier.isPartialThirdParty ? null : [this.carrier.carrierTypeId],
				}),
			},
			...(this.isSuperAdminOrganization && {
				filterCallback: options => {
					return _.reduce(
						options,
						(results, item) => {
							if (item.sellerId === (_.get(this.carrier, 'sellerId') || _.get(this.sellerDropdownStore, 'value.value')))
								results.push({ value: item.id, label: item.name, extra: item });
							return results;
						},
						[]
					);
				},
			}),
		});
	};

	@computed get isTestConnectionDisabled() {
		const { carrierTypeId, shippingAccountId } = this.form.values();
		return (
			(!carrierTypeId && !shippingAccountId) ||
			[carrierTypes.ups, carrierTypes.passport, carrierTypes.ltl].some(item => {
				return (
					_.get(this.carrierTypeDropdownStore, 'value.label') === item ||
					_.get(this.shippingAccountDropdownStore, 'value.extra.carrierType.name') === item
				);
			})
		);
	}

	@computed get selectedCarrierTypeAccount() {
		if (this.form.$('carrierTypeId').value) {
			return _.get(this.carrierTypeDropdownStore, 'value.label');
		}
		if (this.form.$('shippingAccountId').value) {
			return _.get(this.shippingAccountDropdownStore, 'value.extra.carrierType.name');
		}
	}

	@action.bound
	validateCredentials = async () => {
		this.suspend();
		try {
			let data = this.form.values();
			let credentials = await this.prepareCredentialsForValidation(data);
			var testConnectionResult = await this.credentialsValidationStore.validateCarrier({
				isCarrierPartialThirdParty: data.shippingAccountId ? true : false,
				carrierTypeAbrv: this.carrierTypeDropdownStore.value
					? this.carrierTypeDropdownStore.value.extra.abrv
					: this.shippingAccountDropdownStore.value.extra.carrierType.abrv,
				...credentials,
			});
			testConnectionResult.isSuccess
				? this.rootStore.notificationStore.success(['MESSAGES.CARRIER.CONNECTIONVALID'])
				: this.rootStore.notificationStore.error([testConnectionResult.message]);
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CARRIER.CONNECTIONINVALID']);
		}
		this.resume();
	};

	@action.bound
	prepareCredentialsForValidation = async data => {
		if (data.shippingAccountId) {
			if (this.getEditMode() === 'edit') {
				let shippingAccountCredentials = this.shippingAccountDropdownStore.value.extra.json
					? JSON.parse(this.shippingAccountDropdownStore.value.extra.json)
					: this.shippingAccountDropdownStore.value.extra;
				return { ...shippingAccountCredentials, ...data.thirdPartyBilling };
			} else {
				return { ...this.shippingAccountDropdownStore.value.extra, ...data.thirdPartyBilling };
			}
		} else if (this.carrierTypeDropdownStore.value.label === carrierTypes.fedex) {
			return data.fedex;
		} else if (this.carrierTypeDropdownStore.value.label === carrierTypes.ups) {
			return data.ups;
		} else if (this.carrierTypeDropdownStore.value.label === carrierTypes.usps) {
			return data.usps;
		}
	};

	@action.bound
	getCarrier = async (id, options) => {
		await this.carrierStore.getCarrier(id, options);
		let { carrierWarehouses, ...carrier } = this.carrierStore.item; // dont update carrierWarehouses because it breaks multi-select validation
		const carrierCredentials = this.createCredentials(carrier);
		carrier = { ...carrier, ...carrierCredentials };
		this.form.updateForm(carrier, true);
		this.form.$('passport.deliveryDutyPaid').value = carrier.deliveryDutyPaid === 'true';
		this.form.$('surplusCharge').value = carrier.surplusCharge;
		this.form.$('isPercentage').value = carrier.isPercentage;
		this.isThirdParty = carrier.isThirdParty;
		this.isPartialThirdParty = carrier.isPartialThirdParty;
		this.form.$('isPartialThirdParty').value = carrier.isPartialThirdParty;
		this.carrierTypeDisabled = true;
		this.carrier = carrier;
		this.carrier.carrierWarehouses = carrierWarehouses;

		if (carrier.carrierType.name === carrierTypes.ups) {
			this.isExternalAccount = carrier.isExternalAccount;
		}
	};

	@action.bound
	createCredentials = carrier => {
		let carrierCredentials = {};
		if (this.isPartialThirdParty) {
			carrierCredentials = {
				thirdPartyBilling: {
					...carrier.thirdPartyBilling,
				},
			};
		} else {
			if (carrier.carrierType.name === carrierTypes.fedex) {
				carrierCredentials = {
					fedex: {
						clientId: carrier.clientId,
						clientSecret: carrier.clientSecret,
						accountNumber: carrier.accountNumber,
					},
				};
			} else if (carrier.carrierType.name === carrierTypes.ups) {
				carrierCredentials = {
					ups: {
						shipperNumber: carrier.shipperNumber,
						accessLicenseNumber: carrier.accessLicenseNumber,
						refreshToken: carrier.refreshToken,
					},
				};
			} else if (carrier.carrierType.name === carrierTypes.usps) {
				carrierCredentials = {
					usps: {
						refreshToken: carrier.refreshToken,
					},
				};
			} else if (carrier.carrierType.name === carrierTypes.passport) {
				carrierCredentials = {
					passport: {
						apiKey: carrier.apiKey,
						company: carrier.company,
						deliveryDutyPaid: carrier.deliveryDutyPaid,
					},
				};
			}
		}

		return carrierCredentials;
	};

	@action.bound async createOrUpdateCarrier() {
		const {
			ups,
			fedex,
			usps,
			passport,
			carrierTypeId,
			name,
			isPartialThirdParty,
			shippingAccountId,
			thirdPartyBilling,
			surplusCharge,
			isPercentage,
			sellerId,
			carrierWarehouseIds,
		} = this.form.values();
		try {
			let carrierCredentials = {};
			if (this.isThirdParty && isPartialThirdParty) {
				carrierCredentials = {
					thirdPartyBilling: thirdPartyBilling,
				};
			} else if (_.get(this.carrierTypeDropdownStore, 'value.label') === carrierTypes.usps) {
				carrierCredentials = usps;
			} else if (_.get(this.carrierTypeDropdownStore, 'value.label') === carrierTypes.passport) {
				carrierCredentials = passport;
			} else if (_.get(this.carrierTypeDropdownStore, 'value.label') === carrierTypes.fedex) {
				carrierCredentials = fedex;
			} else if (_.get(this.carrierTypeDropdownStore, 'value.label') === carrierTypes.ups) {
				carrierCredentials = ups;
				if (this.isExternalAccount) {
					this.isAuthorized = true;
				}
				carrierCredentials.isExternalAccount = this.isExternalAccount;
			}

			removeNullOrEmptyStringProperties(carrierCredentials);

			const mode = this.getEditMode();
			const carrierWarehouses = _.map(carrierWarehouseIds, warehouseId => {
				return { warehouseId };
			});
			if (mode === 'edit') {
				await this.carrierStore.updateCarrier(
					{
						sellerId: this.carrierStore.item.sellerId,
						id: this.rootStore.routerStore.routerState.queryParams.editId,
						name: name,
						carrierWarehouses,
						surplusCharge: surplusCharge && surplusCharge != '' ? surplusCharge : 0,
						isPercentage: isPercentage,
						carrierTypeId:
							this.isThirdParty && isPartialThirdParty
								? this.shippingAccountDropdownStore.value.extra.carrierTypeId
								: carrierTypeId,
						isThirdParty: this.isThirdParty,
						isPartialThirdParty: this.isThirdParty && isPartialThirdParty,
						shippingAccountId: this.isThirdParty && isPartialThirdParty ? shippingAccountId : null,
						...carrierCredentials,
					},
					sourceOfChange.updateCarrier
				);
				this.rootStore.notificationStore.success(['LOOKUPUPDATE.CARRIER', { name: name }]);
				this.modalParams.success();
			} else if (mode === 'create') {
				if (
					!this.isAuthorized &&
					[carrierTypes.usps, carrierTypes.ups].includes(_.get(this.carrierTypeDropdownStore, 'value.label'))
				) {
					this.form.invalidate(translate('MESSAGES.REQUIRED.CARRIERVIEW.ISAUTHORIZED'));
					return;
				}

				await this.carrierStore.createCarrier(
					{
						sellerId: sellerId,
						name: name,
						carrierWarehouses,
						surplusCharge: surplusCharge && surplusCharge != '' ? surplusCharge : 0,
						isPercentage: isPercentage,
						carrierTypeId:
							this.isThirdParty && isPartialThirdParty
								? this.shippingAccountDropdownStore.value.extra.carrierTypeId
								: carrierTypeId,
						isThirdParty: this.isThirdParty,
						isPartialThirdParty: this.isThirdParty && isPartialThirdParty,
						shippingAccountId: this.isThirdParty && isPartialThirdParty ? shippingAccountId : null,
						abrv: encodeURIComponent(name).toLowerCase(),
						...carrierCredentials,
					},
					sourceOfChange.createCarrier
				);
				this.rootStore.notificationStore.success(['LOOKUPCREATE.CARRIER', { name: name }]);
				this.modalParams.success();
			}
		} catch (err) {
			formInvalidate({ form: this.form, err });
		}
	}

	@action.bound
	onToggleThirdParty = async isThirdParty => {
		this.isThirdParty = isThirdParty;
		this.isPartialThirdParty = !isThirdParty ? false : this.form.$('isPartialThirdParty').value;
		this.setBillToFieldsRequired(this.form.$('isPartialThirdParty').value);
	};

	@action.bound
	isPartialThirdPartyCheck = async event => {
		this.isPartialThirdParty = event.currentTarget.checked;
		if (!this.isEditMode) {
			if (_.get(this.shippingAccountDropdownStore, 'value')) this.shippingAccountDropdownStore.clear();
			if (_.get(this.carrierTypeDropdownStore, 'value')) this.carrierTypeDropdownStore.clear();
		}
		this.setBillToFieldsRequired(this.form.$('isPartialThirdParty').value);
	};

	@action.bound
	setBillToFieldsRequired = async isPartialThirdParty => {
		this.resetBillToFieldsRules();
		if (this.isThirdParty && isPartialThirdParty) {
			this.form.$('carrierTypeId').set('rules', '');
			this.form.$('shippingAccountId').set('rules', 'required');
			this.form.$('thirdPartyBilling.billToAccountNumber').set('rules', 'required');
			this.form.$('thirdPartyBilling.billToAccountZip').set('rules', 'required');
		} else {
			this.form.$('carrierTypeId').set('rules', 'required');
		}

		if (this.carrierTypeDropdownStore.value) {
			this.setRules(carrierTypes.passport, this.setPassportRules);
			this.setRules(carrierTypes.fedex, this.setFedexRules);
		}
	};

	@action.bound
	setRules = (carrierType, setRules) => {
		if (this.carrierTypeDropdownStore.value.label === carrierType) {
			if (this.isThirdParty && this.isPartialThirdParty) {
				setRules(rules.notRequired);
			} else {
				setRules(rules.required);
			}
		} else {
			setRules(rules.notRequired);
		}
	};

	@action.bound
	setPassportRules = value => {
		this.form.$('passport.apiKey').set('rules', value);
		this.form.$('passport.company').set('rules', value);
	};

	@action.bound
	setFedexRules = value => {
		this.form.$('fedex.clientId').set('rules', `${value}`);
		this.form.$('fedex.clientSecret').set('rules', `${value}`);
		this.form.$('fedex.accountNumber').set('rules', `${value}`);
	};

	@action
	setUpsRules = value => {
		this.form.$('ups.clientId').set('rules', value);
		this.form.$('ups.clientSecret').set('rules', value);
		this.form.$('ups.shipperNumber').set('rules', value);
		this.form.$('ups.refreshToken').set('rules', value);
	};

	@action.bound
	resetBillToFieldsRules() {
		this.form.$('shippingAccountId').set('rules', '');
		this.form.$('thirdPartyBilling.billToAccountNumber').set('rules', '');
		this.form.$('thirdPartyBilling.billToAccountZip').set('rules', '');
	}

	getEditMode() {
		const queryParams = this.rootStore.routerStore.routerState.queryParams;
		const mode = queryParams['editId'] !== undefined ? 'edit' : queryParams['create'] !== undefined ? 'create' : 'none';
		this.isEditMode = mode === 'edit' ? true : false;
		return mode;
	}

	clearApiFields = () => {
		this.form.$('ups').clear();
		this.form.$('fedex').clear();
	};

	@action.bound
	onCarrierTypeChange = (selectedOption, clearApiFields = false) => {
		if (_.get(selectedOption, 'label') === carrierTypes.passport) {
			this.setPassportRules(rules.required);
			this.setUpsRules(rules.notRequired);
		} else {
			this.setPassportRules(rules.notRequired);
		}
		if (_.get(selectedOption, 'label') === carrierTypes.fedex) {
			this.setFedexRules(rules.required);
			this.setUpsRules(rules.notRequired);
		} else {
			this.setFedexRules(rules.notRequired);
		}
		if (clearApiFields) this.clearApiFields();

		if (!this.isThirdParty || !this.isPartialThirdParty) this.shippingAccountDropdownStore.clear();
	};

	@action.bound
	onSelectSeller = async selectedOption => {
		this.shippingAccountDropdownStore.options = this.shippingAccountDropdownStore.store.response.item
			.filter(item => item.sellerId === selectedOption.value)
			.map(item => ({ value: item.id, label: item.name, extra: item }));
		this.warehouseDropdownStore.clear();
		await this.warehouseDropdownStore.updateFilter({
			sellerWarehousesOnlyOrganizationId: selectedOption.value,
			includeOnlySellerWarehouses: true,
		});
		if (_.get(this.shippingAccountDropdownStore, 'value')) this.shippingAccountDropdownStore.clear();
	};

	@action.bound
	async onAuthorizeStamps() {
		try {
			await this.carrierStore.authorizeStamps();
			this.openConfirmationModal(this.getAuthorizeStampsConfirmModalProps());
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	getAuthorizeStampsConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				// Get authorization results from Redis.
				try {
					var response = await this.carrierStore.getAuthorizationResultStamps();
					if (isSome(response)) {
						this.form.$('usps.refreshToken').value = response.data;
						this.isAuthorized = true;
						this.rootStore.notificationStore.success(['OAUTH.STAMPS.SUCCESSFORMUPDATE']);
					} else {
						this.rootStore.notificationStore.error(['OAUTH.STAMPS.FAILGETREFRESHTOKEN']);
					}
				} catch (err) {
					notificationError({ store: this.rootStore.notificationStore, err });
				}
			},
			message: ['OAUTH.STAMPS.CONFIRMSUCCESSAUTH'],
		};
	};

	@action.bound
	async onAuthorizeUps() {
		try {
			await this.carrierStore.authorizeUps();
			this.openConfirmationModal(this.getAuthorizeUpsConfirmModalProps());
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	getAuthorizeUpsConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				// Get Refresh Token from Redis.
				try {
					var response = await this.carrierStore.getAuthorizationResultUps();
					if (isSome(response)) {
						this.form.$('ups.refreshToken').value = response.data;
						this.isAuthorized = true;
						this.rootStore.notificationStore.success(['OAUTH.UPS.SUCCESSFORMUPDATE']);
					} else {
						this.rootStore.notificationStore.error(['OAUTH.UPS.FAILGETREFRESHTOKEN']);
					}
				} catch (err) {
					notificationError({ store: this.rootStore.notificationStore, err });
				}
			},
			message: ['OAUTH.UPS.CONFIRMSUCCESSAUTH'],
		};
	};

	@action
	setIsExternalAccount = isExternalAccount => {
		this.isExternalAccount = isExternalAccount;
	};
}

const rules = {
	notRequired: '',
	required: 'required',
};

export default CarrierEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/carrier/stores/CarrierEditViewStore.js